/**
 * K17 T1.12 — AI Quotas Management (systemadmin-side)
 *
 * Fullstendig oversikt over KI-kvoter, forbruk, historikk og fakturering
 * per medlemsorganisasjon. Kun tilgjengelig for systemadmin (backend filtrerer).
 *
 * Funksjonalitet:
 *   - Liste alle aktive medlemmer med gjeldende måneds kvote og forbruk
 *   - Endre tokens_included + overage_policy per medlem via modal
 *   - Se aktiv pricing (usd_to_nok, margin, fast pris per 1M tokens)
 *   - Legge til ny pricing (versjonert)
 */
import { useState, useEffect } from 'react';
import { AlertCircle, TrendingUp, Settings2, Save, X, DollarSign, Plus, Receipt, BarChart3 } from 'lucide-react';
import { authenticatedFetch } from '../../lib/auth';
import AiInvoicesList from './AiInvoicesList';

interface Quota {
  member_id: string;
  organization_name: string;
  status: string;
  study_association_name: string | null;
  tokens_included: number | null;
  tokens_used: number | null;
  cost_nok: number | null;
  cost_usd: number | null;
  tokens_used_subsidized: number | null;
  cost_nok_subsidized: number | null;
  overage_policy: string | null;
  warned_80pct_at: string | null;
  warned_100pct_at: string | null;
  blocked_at: string | null;
  last_aggregated_at: string | null;
}

interface Pricing {
  id: string;
  effective_from: string;
  effective_to: string | null;
  usd_to_nok: number;
  margin_multiplier: number;
  fixed_price_per_1m_tokens_nok: number | null;
  notes: string | null;
}

interface QuotaResponse {
  current_period: { year: number; month: number };
  members: Quota[];
  active_pricing: Pricing | null;
}

function fmt(n: number | null | undefined): string {
  if (n == null) return '0';
  return Number(n).toLocaleString('no-NO');
}

const POLICY_LABELS: Record<string, string> = {
  block: 'Blokker ved 100%',
  warn_only: 'Kun varsel (ingen fakturering)',
  warn_then_bill: 'Varsel + kvartalsvis fakturering'
};

type Tab = 'quotas' | 'invoices';

export default function AiQuotasManagement() {
  const [activeTab, setActiveTab] = useState<Tab>('quotas');
  const [data, setData] = useState<QuotaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Quota | null>(null);
  const [editValues, setEditValues] = useState<{ tokens_included: number; overage_policy: string }>({
    tokens_included: 1000000,
    overage_policy: 'warn_then_bill'
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [pricingHistory, setPricingHistory] = useState<Pricing[]>([]);
  const [newPricing, setNewPricing] = useState({
    effective_from: '',
    usd_to_nok: 11.0,
    margin_multiplier: 2.0,
    fixed_price_per_1m_tokens_nok: 150,
    notes: ''
  });

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch('/api/admin/ai-quotas');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openEdit(q: Quota) {
    setEditing(q);
    setEditValues({
      tokens_included: q.tokens_included ?? 1000000,
      overage_policy: q.overage_policy ?? 'warn_then_bill'
    });
    setSaveError(null);
  }

  async function saveEdit() {
    if (!editing) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await authenticatedFetch(`/api/admin/ai-quotas/${editing.member_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editValues)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'unknown');
    } finally {
      setSaving(false);
    }
  }

  async function openPricingModal() {
    try {
      const res = await authenticatedFetch('/api/admin/ai-quotas/pricing');
      if (res.ok) {
        const json = await res.json();
        setPricingHistory(json.pricing_history || []);
      }
    } catch (e) {
      console.warn('Kunne ikke hente pricing-historikk:', e);
    }
    if (data?.active_pricing) {
      setNewPricing({
        effective_from: '',
        usd_to_nok: data.active_pricing.usd_to_nok,
        margin_multiplier: data.active_pricing.margin_multiplier,
        fixed_price_per_1m_tokens_nok: data.active_pricing.fixed_price_per_1m_tokens_nok ?? 150,
        notes: ''
      });
    }
    setPricingModalOpen(true);
  }

  async function savePricing() {
    if (!newPricing.effective_from) {
      setSaveError('Effektivdato må settes');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await authenticatedFetch('/api/admin/ai-quotas/pricing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPricing)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setPricingModalOpen(false);
      await load();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'unknown');
    } finally {
      setSaving(false);
    }
  }

  if (activeTab === 'invoices') {
    return (
      <div className="space-y-6">
        <TabBar activeTab={activeTab} onChange={setActiveTab} />
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Fakturaer</h2>
          <p className="text-sm text-gray-600 mt-1">
            Oversikt over KI-topup-fakturaer. Marker en faktura som betalt for å nullstille tokens-forbruket for perioden umiddelbart.
          </p>
        </div>
        <AiInvoicesList />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <TabBar activeTab={activeTab} onChange={setActiveTab} />
        <div className="p-6 text-gray-600">Laster KI-kvoter…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-6">
        <TabBar activeTab={activeTab} onChange={setActiveTab} />
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-800">
          <div className="flex items-center gap-2 font-medium mb-1">
            <AlertCircle className="h-4 w-4" /> Kunne ikke hente kvoter
          </div>
          <div className="text-sm">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const monthLabel = `${String(data.current_period.month).padStart(2, '0')}/${data.current_period.year}`;

  return (
    <div className="space-y-6">
      <TabBar activeTab={activeTab} onChange={setActiveTab} />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">KI-kvoter og fakturering</h2>
          <p className="text-sm text-gray-600 mt-1">
            Inneværende måned: <span className="font-medium">{monthLabel}</span>. Fri-kvote per medlem: 100 000 tokens/mnd.
            Faktura utstedes automatisk ved 80 % av kvoten. 110 % = hard cut-off til betaling er registrert.
          </p>
        </div>
        <button
          onClick={openPricingModal}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-800"
        >
          <DollarSign className="h-4 w-4" /> Pris­struktur
        </button>
      </div>

      {data.active_pricing && (
        <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
          <div className="text-sm text-kf-blue-900">
            <span className="font-medium">Aktiv prising</span> (fra {data.active_pricing.effective_from}):
            USD/NOK <b>{data.active_pricing.usd_to_nok}</b>, margin <b>{data.active_pricing.margin_multiplier}x</b>,
            fast pris {data.active_pricing.fixed_price_per_1m_tokens_nok ? <b>{data.active_pricing.fixed_price_per_1m_tokens_nok} NOK / 1M tokens</b> : 'ikke satt'}.
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organisasjon</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Studieforbund</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Kvote</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Brukt</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">%</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">NOK</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" title="KI-validering — dekket av Kompetanseforbundet">Validering (gratis)</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Policy</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.members.map(m => {
              const used = m.tokens_used ?? 0;
              const inc = m.tokens_included ?? 1000000;
              const pct = inc > 0 ? Math.round((used / inc) * 100) : 0;
              const pctClass = pct >= 100 ? 'text-red-600 font-semibold' : pct >= 80 ? 'text-kf-orange-600 font-medium' : 'text-gray-700';

              return (
                <tr key={m.member_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.organization_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{m.study_association_name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{fmt(inc)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{fmt(used)}</td>
                  <td className={`px-4 py-3 text-sm text-right ${pctClass}`}>{pct}%</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{Number(m.cost_nok ?? 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-500" title={`Systemkost: ${Number(m.cost_nok_subsidized ?? 0).toFixed(2)} NOK`}>
                    {fmt(m.tokens_used_subsidized)}
                    <span className="text-xs text-gray-400 ml-1">({Number(m.cost_nok_subsidized ?? 0).toFixed(0)} kr)</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{POLICY_LABELS[m.overage_policy || 'warn_then_bill']}</td>
                  <td className="px-4 py-3 text-xs">
                    {m.blocked_at && <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 px-2 py-0.5 rounded-full"><AlertCircle className="h-3 w-3" /> Blokkert</span>}
                    {!m.blocked_at && m.warned_100pct_at && <span className="inline-flex items-center gap-1 text-kf-orange-700 bg-kf-orange-50 px-2 py-0.5 rounded-full"><TrendingUp className="h-3 w-3" /> Over 100%</span>}
                    {!m.blocked_at && !m.warned_100pct_at && m.warned_80pct_at && <span className="inline-flex items-center gap-1 text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded-full">80%-varsel sendt</span>}
                    {!m.blocked_at && !m.warned_100pct_at && !m.warned_80pct_at && <span className="text-emerald-700">Innenfor</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(m)} className="text-sm text-kf-blue-600 hover:text-kf-blue-800 inline-flex items-center gap-1">
                      <Settings2 className="h-4 w-4" /> Endre
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit-modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-4 sm:p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Endre kvote — {editing.organization_name}</h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tokens per måned (fri-kvote)</label>
                <input
                  type="number"
                  min={0}
                  step={10000}
                  value={editValues.tokens_included}
                  onChange={e => setEditValues(v => ({ ...v, tokens_included: parseInt(e.target.value || '0', 10) }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Standard: 100 000. Sett til 0 for å blokkere fri-bruk.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Overføringspolicy</label>
                <select
                  value={editValues.overage_policy}
                  onChange={e => setEditValues(v => ({ ...v, overage_policy: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="block">Blokker (ingen KI-kall over kvote)</option>
                  <option value="warn_only">Kun varsel (ingen fakturering)</option>
                  <option value="warn_then_bill">Varsel + kvartalsvis fakturering</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  <b>Blokker</b>: KI-kall avvises ved 100%. <b>Varsel</b>: bare 80/100%-e-post. <b>Fakturering</b>: overforbruk regnes til kvartalsfaktura.
                </p>
              </div>

              {saveError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                  <AlertCircle className="h-4 w-4 inline mr-1" /> {saveError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">Avbryt</button>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded-md disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Lagrer…' : 'Lagre'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* (End of main content for quotas tab) */}

      {/* Pricing-modal */}
      {pricingModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setPricingModalOpen(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Prisstruktur</h3>
              <button onClick={() => setPricingModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-600">
                Prising bestemmer NOK-verdi per 1M tokens som brukes ved kvartalsvis fakturering. Endring gjelder fra angitt dato. Tidligere aktiv rad avsluttes automatisk dagen før den nye trer i kraft.
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ikrafttredelse</label>
                  <input
                    type="date"
                    value={newPricing.effective_from}
                    onChange={e => setNewPricing(p => ({ ...p, effective_from: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">USD/NOK-kurs</label>
                  <input
                    type="number"
                    step={0.001}
                    value={newPricing.usd_to_nok}
                    onChange={e => setNewPricing(p => ({ ...p, usd_to_nok: parseFloat(e.target.value || '0') }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Margin (multiplikator)</label>
                  <input
                    type="number"
                    step={0.1}
                    value={newPricing.margin_multiplier}
                    onChange={e => setNewPricing(p => ({ ...p, margin_multiplier: parseFloat(e.target.value || '0') }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fast pris per 1M tokens (NOK)</label>
                  <input
                    type="number"
                    step={1}
                    value={newPricing.fixed_price_per_1m_tokens_nok}
                    onChange={e => setNewPricing(p => ({ ...p, fixed_price_per_1m_tokens_nok: parseFloat(e.target.value || '0') }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notat</label>
                <textarea
                  rows={2}
                  value={newPricing.notes}
                  onChange={e => setNewPricing(p => ({ ...p, notes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="F.eks. begrunnelse for pris­endring"
                />
              </div>

              {saveError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                  <AlertCircle className="h-4 w-4 inline mr-1" /> {saveError}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button onClick={() => setPricingModalOpen(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">Avbryt</button>
                <button
                  onClick={savePricing}
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded-md disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {saving ? 'Lagrer…' : 'Lagre ny prising'}
                </button>
              </div>

              {pricingHistory.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-sm font-medium text-gray-700 mb-2">Historikk</div>
                  <div className="space-y-1 text-xs text-gray-600">
                    {pricingHistory.map(p => (
                      <div key={p.id} className="flex items-center gap-3 py-1">
                        <span className="font-mono w-32">{p.effective_from}</span>
                        <span>USD/NOK {p.usd_to_nok}</span>
                        <span>Margin {p.margin_multiplier}x</span>
                        {p.fixed_price_per_1m_tokens_nok && <span>Fast {p.fixed_price_per_1m_tokens_nok} NOK/1M</span>}
                        {p.effective_to && <span className="text-gray-400">→ {p.effective_to}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBar({ activeTab, onChange }: { activeTab: Tab; onChange: (t: Tab) => void }) {
  return (
    <div className="border-b border-gray-200">
      <nav className="-mb-px flex gap-6">
        <TabButton
          active={activeTab === 'quotas'}
          onClick={() => onChange('quotas')}
          icon={<BarChart3 className="h-4 w-4" />}
        >
          Kvoter og forbruk
        </TabButton>
        <TabButton
          active={activeTab === 'invoices'}
          onClick={() => onChange('invoices')}
          icon={<Receipt className="h-4 w-4" />}
        >
          Fakturaer
        </TabButton>
      </nav>
    </div>
  );
}

function TabButton({
  active, onClick, icon, children,
}: {
  active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
        active
          ? 'border-kf-primary text-kf-primary'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}
